/**
 * Nahlasit problem — zalozi tiket u RedHand podpory.
 *
 * BEZPECNOST (krok MOB1): prohlizec uz NEVOLA podporu, ani neprimo pres
 * nginx. Posila na VLASTNI API REOS (`POST /api/support/report`), ktere drzi
 * token podpory na serveru a doplni uzivatele i kancelar z prihlasene session
 * — hlaseni tedy nejde podepsat cizim jmenem a token se do prohlizece
 * nedostane. Do MOB1 token dopisoval nginx (`location = /api/podpora/tickets`),
 * coz fungovalo jen na preview a v nativnim obalu by nefungovalo vubec.
 *
 * BEZ ZAPOJENI NENI VIDET: `flags.supportReporting` z `GET /api/me` rika, jestli
 * ma server `PODPORA_API_URL` + `PODPORA_TOKEN_REOS`. Kdyz ne, komponenta
 * nevykresli NIC (zadani MOB1: „zadna falesna funkce"). Tataz podminka plati
 * pro polozku v nabidce profilu i v paticce panelu — cte se z JEDNOHO mista
 * (`useSupportReporting`), aby nemohlo vzniknout tlacitko, ktere je videt a
 * nefunguje.
 *
 * Kontext se pripoji sam: adresa obrazovky (vcetne `?sekce=`), slug zakazky,
 * verze, prohlizec a poslednich 20 chyb konzole a neuspesnych volani
 * (`lib/report-collector.ts` — bez obsahu tel a bez tokenu). `fingerprint`
 * slouzi k deduplikaci na strane podpory: stejny problem ze stejne obrazovky
 * nezalozi druhy tiket, jen pricte pocitadlo.
 *
 * Snimky obrazovky (az ctyri) se zmensuji V PROHLIZECI (`lib/resize-image.ts`,
 * tataz funkce jako „Vyfotit" u nemovitosti) — fotka z telefonu ma bezne 5 MB
 * a hlaseni by se na mobilnim pripojeni neodeslalo.
 */

import * as React from 'react';
import { useSession } from '../stores/session.js';
import {
  installReportCollector,
  recentConsoleErrors,
  recentFailedRequests,
} from '../lib/report-collector.js';
import { resizeImageToJpegBase64, type PreparedImage } from '../lib/resize-image.js';
import { appVersion } from '../lib/pwa.js';
import { SUPPORT_TEXTS, SUPPORT_SEVERITIES, type SupportSeverity } from '../lib/texts.js';

export interface SupportWidgetProps {
  /**
   * Krok UXA1: ovladane otevreni z avatar menu / paticky sidebaru (R.B.
   * 1. 10.: plovouci tlacitko prekryvalo obsah na mobilu, FIX4). Bez props
   * se widget chova jako driv — vlastni plovouci tlacitko + vlastni stav
   * (zustava pro `SupportWidget.spec.tsx`, ktery komponentu renderuje bez
   * okolniho layoutu).
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

const ENDPOINT_PATH = '/api/support/report';
const MAX_IMAGES = 4;

const baseUrl =
  (import.meta as unknown as { env: Record<string, string | undefined> }).env.VITE_API_URL ??
  'http://localhost:3000';

interface ReportResponse {
  readonly ok?: boolean;
  readonly ticketId?: string;
  readonly deduplicated?: boolean;
  /** problem+json ze serveru. */
  readonly detail?: string;
  readonly title?: string;
}

/**
 * Nahled se kresli z TEHOZ base64, ktere pujde na server — ne z
 * `URL.createObjectURL`. Duvod je CSP administrace: `img-src 'self' data:
 * https:` adresy `blob:` nepousti, takze nahled z objektove adresy by byl
 * rozbity obrazek (nalez ze ziveho overeni MOB1). Navic je tak videt presne
 * to, co se odesila — po zmenseni, ne puvodni soubor.
 */
type Screenshot = PreparedImage;

/**
 * JEDINA podminka viditelnosti hlaseni problemu. Pouziva ji widget i obe
 * mista, ktera ho spousteji (`layout.tsx`: nabidka profilu, paticka panelu).
 */
export function useSupportReporting(): boolean {
  return useSession((s) => s.supportReporting);
}

/** Slug zakazky z adresy (/zakazka/<slug>), jinak null — zadne domysleni. */
function caseSlugFromPath(pathname: string): string | null {
  const match = /^\/zakazka\/([^/?#]+)/.exec(pathname);
  return match?.[1] ?? null;
}

/**
 * Otisk pro deduplikaci: obrazovka + predmet. Zamerne NEobsahuje telo — dva
 * hlasy o tomtez problemu z teze obrazovky maji splynout do jednoho tiketu.
 * Server pocita totez (`supportFingerprint` v `routes/support-report.ts`);
 * posila ho klient, aby i on videl, co se odesila.
 */
function fingerprintOf(pathname: string, subject: string): string {
  const normalized = subject.trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 80);
  return `reos:${pathname}:${normalized}`;
}

export function SupportWidget({ open: controlledOpen, onOpenChange }: SupportWidgetProps = {}): React.JSX.Element {
  const available = useSupportReporting();
  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (isControlled) onOpenChange?.(next);
      else setInternalOpen(next);
    },
    [isControlled, onOpenChange],
  );
  const [severity, setSeverity] = React.useState<SupportSeverity>('P3');
  const [subject, setSubject] = React.useState('');
  const [body, setBody] = React.useState('');
  const [images, setImages] = React.useState<readonly Screenshot[]>([]);
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState<{ ok: boolean; text: string } | null>(null);
  const fileInput = React.useRef<HTMLInputElement>(null);

  // Sber chyb musi bezet uz PRED otevrenim panelu — jinak by hlaseni
  // neobsahovalo prave tu chybu, kvuli ktere ho uzivatel pise.
  React.useEffect(() => {
    if (available) installReportCollector();
  }, [available]);

  const close = (): void => {
    setOpen(false);
    setResult(null);
  };

  const addImages = (files: FileList | null): void => {
    if (files === null || files.length === 0) return;
    const room = MAX_IMAGES - images.length;
    if (room <= 0) return;
    void Promise.all(Array.from(files).slice(0, room).map((file) => resizeImageToJpegBase64(file)))
      .then((prepared) => { setImages((prev) => [...prev, ...prepared]); })
      .catch(() => { setResult({ ok: false, text: SUPPORT_TEXTS.screenshotFailed }); });
  };

  const removeImage = (index: number): void => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const send = async (): Promise<void> => {
    if (subject.trim() === '' || body.trim() === '') {
      setResult({ ok: false, text: SUPPORT_TEXTS.validation });
      return;
    }
    setSending(true);
    setResult(null);
    const pathname = window.location.pathname;
    const slug = caseSlugFromPath(pathname);
    try {
      const res = await fetch(`${baseUrl}${ENDPOINT_PATH}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          severity,
          subject: subject.trim(),
          body: body.trim(),
          contextUrl: window.location.href,
          ...(slug !== null ? { caseSlug: slug } : {}),
          fingerprint: fingerprintOf(pathname, subject),
          browser: window.navigator.userAgent,
          // Verze aplikace v prohlizeci; server k tomu pripoji svou vlastni —
          // kdyz se lisi, uzivatel bezi na stare zakeslovane verzi.
          appVersion: appVersion(),
          consoleErrors: recentConsoleErrors(),
          failedRequests: recentFailedRequests(),
          attachments: images.map(({ name, contentType, dataBase64 }) => ({
            name,
            contentType,
            dataBase64,
          })),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as ReportResponse;
      if (!res.ok || data.ok !== true) {
        const detail = data.detail ?? data.title ?? `HTTP ${String(res.status)}`;
        setResult({ ok: false, text: `${SUPPORT_TEXTS.failed} ${detail}` });
        return;
      }
      const id = data.ticketId ?? '?';
      setResult({
        ok: true,
        text:
          data.deduplicated === true
            ? `${SUPPORT_TEXTS.sentDuplicate} ${id}`
            : `${SUPPORT_TEXTS.sent} ${id}`,
      });
      setSubject('');
      setBody('');
      setImages([]);
    } catch {
      // Sit selhala — nikdy netvrdit, ze tiket vznikl.
      setResult({ ok: false, text: SUPPORT_TEXTS.offline });
    } finally {
      setSending(false);
    }
  };

  // Bez zapojeni na serveru se hlaseni nenabizi vubec (zadani MOB1 bod 3).
  if (!available) return <></>;

  if (!open) {
    // Ovladany rezim (avatar menu / paticka sidebaru): o otevreni rozhoduje
    // volajici, widget zde zadne vlastni plovouci tlacitko nekresli (R.B.
    // 1. 10.: prekryvalo obsah na mobilu).
    if (isControlled) return <></>;
    return (
      <button
        type="button"
        className="rh-btn"
        onClick={() => { setOpen(true); }}
        data-testid="support-open"
        aria-haspopup="dialog"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          zIndex: 60,
          borderRadius: 999,
          boxShadow: '0 6px 20px var(--rh-shadow-menu)',
          background: 'var(--rh-text)',
          color: 'var(--rh-card)',
          padding: '10px 16px',
        }}
      >
        {SUPPORT_TEXTS.openButton}
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label={SUPPORT_TEXTS.title}
      data-testid="support-panel"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        zIndex: 60,
        width: 360,
        maxWidth: 'calc(100vw - 40px)',
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
        background: 'var(--rh-card)',
        border: '1px solid var(--rh-border)',
        borderRadius: 12,
        boxShadow: '0 10px 30px var(--rh-shadow-menu)',
        padding: 16,
      }}
    >
      <div className="flex items-baseline justify-between gap-2" style={{ marginBottom: 6 }}>
        <strong style={{ fontSize: 'var(--rh-size-subhead)' }}>{SUPPORT_TEXTS.title}</strong>
        <button type="button" className="rh-btn" onClick={close} data-testid="support-close">
          {SUPPORT_TEXTS.close}
        </button>
      </div>
      <p style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', margin: '0 0 10px' }}>{SUPPORT_TEXTS.hint}</p>

      <label style={{ display: 'block', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }}>
        {SUPPORT_TEXTS.severityLabel}
        <select
          value={severity}
          onChange={(e) => { setSeverity(e.target.value as SupportSeverity); }}
          data-testid="support-severity"
          style={{ width: '100%', marginTop: 4, padding: 6, borderRadius: 8, border: '1px solid var(--rh-border)' }}
        >
          {SUPPORT_SEVERITIES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
      </label>

      <label style={{ display: 'block', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', marginTop: 8 }}>
        {SUPPORT_TEXTS.subjectLabel}
        <input
          value={subject}
          onChange={(e) => { setSubject(e.target.value); }}
          data-testid="support-subject"
          maxLength={200}
          style={{ width: '100%', marginTop: 4, padding: 6, borderRadius: 8, border: '1px solid var(--rh-border)' }}
        />
      </label>

      <label style={{ display: 'block', fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)', marginTop: 8 }}>
        {SUPPORT_TEXTS.bodyLabel}
        <textarea
          value={body}
          onChange={(e) => { setBody(e.target.value); }}
          data-testid="support-body"
          rows={5}
          style={{ width: '100%', marginTop: 4, padding: 6, borderRadius: 8, border: '1px solid var(--rh-border)', resize: 'vertical' }}
        />
      </label>

      {/* Snimky obrazovky — na telefonu zaroven fotoaparat (`capture` nechavame
          na uzivateli: snimek obrazovky si vybira z galerie). */}
      <div style={{ marginTop: 8 }}>
        <span style={{ fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }}>
          {SUPPORT_TEXTS.screenshotsLabel}
        </span>
        <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
          <button
            type="button"
            className="rh-btn"
            onClick={() => fileInput.current?.click()}
            disabled={sending || images.length >= MAX_IMAGES}
            data-testid="support-add-screenshot"
          >
            {SUPPORT_TEXTS.addScreenshot}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            aria-label={SUPPORT_TEXTS.addScreenshot}
            data-testid="support-screenshot-input"
            onChange={(event) => {
              addImages(event.target.value === '' ? null : event.target.files);
              event.target.value = '';
            }}
          />
        </div>
        {images.length > 0 ? (
          <ul
            className="flex flex-wrap gap-2 m-0 p-0 list-none"
            style={{ marginTop: 6 }}
            data-testid="support-screenshots"
          >
            {images.map((image, index) => (
              <li key={`${String(index)}-${image.name}`} style={{ position: 'relative' }}>
                <img
                  src={`data:${image.contentType};base64,${image.dataBase64}`}
                  alt={SUPPORT_TEXTS.screenshotAlt}
                  style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--rh-border)' }}
                />
                <button
                  type="button"
                  className="rh-btn"
                  onClick={() => { removeImage(index); }}
                  disabled={sending}
                  aria-label={SUPPORT_TEXTS.removeScreenshot}
                  title={SUPPORT_TEXTS.removeScreenshot}
                  data-testid={`support-remove-screenshot-${String(index)}`}
                  style={{ position: 'absolute', top: -8, right: -8, padding: '0 6px', borderRadius: 999 }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <p style={{ fontSize: 'var(--rh-size-tag)', color: 'var(--rh-icon-muted)', margin: '8px 0 0' }} data-testid="support-context">
        {SUPPORT_TEXTS.contextNote}
      </p>

      <div className="flex items-center gap-2" style={{ marginTop: 10 }}>
        <button
          type="button"
          className="rh-btn rh-btn--primary"
          disabled={sending}
          onClick={() => { void send(); }}
          data-testid="support-send"
        >
          {sending ? SUPPORT_TEXTS.sending : SUPPORT_TEXTS.send}
        </button>
        {result !== null && (
          <span
            data-testid="support-result"
            style={{ fontSize: 'var(--rh-size-meta)', color: result.ok ? 'var(--rh-success)' : 'var(--rh-warning)' }}
          >
            {result.text}
          </span>
        )}
      </div>
    </div>
  );
}
